import { formatMoney } from '@/helpers/common';
import { AbstractModel } from '@/models/abstractModel';
import { useBookmakerDictionary, useCountryDictionary, useProxyDictionary } from '@/stores';
import type { IMonitoringBetting } from '@/types';

export class MonitoringBetting extends AbstractModel {
    public accountCount: number;
    public brlAccountActiveBalance: number | null;
    public brlAccountActiveCount: number;
    public comment?: string;
    public eurAccountActiveBalance: number | null;
    public eurAccountActiveCount: number;
    public groupProxyCount: number;
    public groupProxyCountryIds: number[];
    public hkdAccountActiveBalance: number | null;
    public hkdAccountActiveCount: number;
    public proxyUsing: string;
    public siteId: number;

    constructor(data: IMonitoringBetting) {
        super();
        this.siteId = data.site_id;
        this.groupProxyCountryIds = data.group_proxy_country_ids;
        this.brlAccountActiveCount = data.brl_account_active_count;
        this.eurAccountActiveCount = data.eur_account_active_count;
        this.hkdAccountActiveCount = data.hkd_account_active_count;
        this.accountCount = data.account_count;
        this.groupProxyCount = data.group_proxy_count;
        this.brlAccountActiveBalance = data.brl_account_active_balance;
        this.eurAccountActiveBalance = data.eur_account_active_balance;
        this.hkdAccountActiveBalance = data.hkd_account_active_balance;
        this.proxyUsing = data.proxy_using;
        this.comment = data.comment;
    }

    get bookmakerName(): string {
        try {
            const bookmakerDictionary = useBookmakerDictionary();
            return bookmakerDictionary.nameById(this.siteId) || '';
        } catch {
            return '';
        }
    }

    get countryCodesDisplay(): string {
        if (!this.groupProxyCountryIds || this.groupProxyCountryIds.length === 0) return '';
        try {
            const countryDictionary = useCountryDictionary();
            return this.groupProxyCountryIds
                .map((id) => {
                    try {
                        const country = countryDictionary.itemById(id);
                        return country?.iso3?.toUpperCase() || '';
                    } catch {
                        return '';
                    }
                })
                .filter((code) => code)
                .join(', ');
        } catch {
            return '';
        }
    }

    get formattedCreditBRL(): string | null {
        if (this.brlAccountActiveBalance === null) return null;
        return formatMoney(this.brlAccountActiveBalance, 0);
    }

    get formattedCreditEUR(): string | null {
        if (this.eurAccountActiveBalance === null) return null;
        return formatMoney(this.eurAccountActiveBalance, 0);
    }

    get formattedCreditHKD(): string | null {
        if (this.hkdAccountActiveBalance === null) return null;
        return formatMoney(this.hkdAccountActiveBalance, 0);
    }

    get maxSharing(): number {
        return this.proxyUsingParsed.max_proxy_sharing || 0;
    }

    get maxSharingTooltip(): string {
        const proxyName = this.mostUsedProxyName;
        return proxyName ? `Proxy ${proxyName}` : '';
    }

    get mostUsedProxyName(): string {
        const proxyId = this.proxyUsingParsed.most_use_proxy_id;
        if (!proxyId) return '';
        try {
            const proxyDictionary = useProxyDictionary();
            return proxyDictionary.nameById(proxyId) || '';
        } catch {
            return '';
        }
    }

    get proxyUsingParsed(): { max_proxy_sharing?: number; most_use_proxy_id?: number } {
        if (!this.proxyUsing) return {};
        try {
            return JSON.parse(this.proxyUsing);
        } catch {
            return {};
        }
    }

    get statusClass(): string {
        return this.accountCount > 0 ? 'status-green' : 'status-red';
    }
}
